import { useState } from "react";
import { G, GL, GB, BLACK, MID, LIGHT, BORDER, BG, BENCHMARKS, calcPriority, sigConfidence } from "../constants";
import { FEED_HEALTH, KEYWORD_GAPS } from "../data";
import { Ring, SevBadge, Tag, SourceLogo, BigRing } from "../components/shared";
import { PageHead, Footer } from "../components/Shell";
import { SOURCE_META } from "../data";

export function S06({ sel, fixes, onNext, products, generatedCopy = {} }) {
  const prods   = products.filter(p => sel.includes(p.id));
  const totFix  = Object.values(fixes).filter(Boolean).length;
  const totSig  = prods.reduce((s, p) => s + (p.signalAttribution?.length || 0), 0);
  const totRev  = prods.reduce((s, p) => s + p.impact.revenue, 0);
  const totImp  = prods.reduce((s, p) => s + (p.impact.impAfter - p.impact.impBefore), 0);

  // Feed score: start at 41 (FEED_HEALTH baseline), add points per selected SKU
  const scoreAfter = Math.min(100, 41
    + (prods.length >= 1 ? 12 : 0)   // titles optimised
    + (totFix > 0 ? 10 : 0)           // GMC fixes
    + (totSig > 0 ? 8 : 0)            // signals embedded
    + (prods.length >= 2 ? 4 : 0)     // batch size bonus
  );
  const scoreBefore = 41;

  const [animated, setAnimated] = useState(false);
  const [activeTab, setActiveTab] = useState("audit");

  useState(() => { const t = setTimeout(() => setAnimated(true), 300); return () => clearTimeout(t); });

  const scoreColor = n => n >= 70 ? G : n >= 50 ? "#d97706" : "#dc2626";

  const dimensions = [
    { label: "Title quality",          before: 34, after: Math.min(100, 34 + (prods.length >= 1 ? 38 : 0)),  why: "Optimised titles with brand, material, fit and key attribute structure" },
    { label: "Description coverage",   before: 18, after: Math.min(100, 18 + (prods.length >= 1 ? 52 : 0)),  why: "Signal-driven descriptions written for all selected SKUs" },
    { label: "Metafield completeness", before: 28, after: Math.min(100, 28 + (prods.length >= 1 ? 32 : 0)),  why: "Material, fit, weight and care metafields populated and structured" },
    { label: "Taxonomy & tagging",     before: 32, after: Math.min(100, 32 + (prods.length >= 1 ? 24 : 0)),  why: "Product type set, tags standardised with fit, season and fabric taxonomy" },
    { label: "SEO fields",             before: 12, after: Math.min(100, 12 + (prods.length >= 1 ? 38 : 0)),  why: "Meta title and meta description generated for all selected SKUs" },
    { label: "Signal coverage",        before: 67, after: Math.min(100, 67 + (prods.length >= 1 ? 22 : 0)),  why: "Consumer-native phrases embedded across title, description and meta fields" },
  ];

  // What automation can't fix - manual actions remaining
  const manualActions = [
    {
      priority: "high",
      icon: "📸",
      title: "Additional product photography",
      detail: `${prods.length > 1 ? prods.length + " SKUs have" : "This SKU has"} only 1 product image. Google Shopping listings with 3+ images see 26% higher CTR. Shoot on-model, flat lay, and detail/texture shots for each.`,
      impact: "Est. +15-25% CTR on Shopping placements",
      actions: ["On-model shot (front + back)", "Flat lay on neutral background", "Material / texture close-up", "Lifestyle / styled outfit context"],
    },
    {
      priority: "high",
      icon: "⭐",
      title: "Product reviews and social proof",
      detail: "No verified reviews on these SKUs. LLMs weight products with reviews significantly higher when generating recommendations. Google Shopping also surfaces star ratings as a Shopping filter.",
      impact: "Reviews needed before LLM citation is likely",
      actions: ["Enable post-purchase review emails in Shopify", "Add review widget to product page", "Consider seeding with press or early customers", "Add schema markup for review stars in SERPs"],
    },
    {
      priority: "med",
      icon: "📐",
      title: "Size and fit guide",
      detail: "No size guide or fit notes on these product pages. 'Oversized fit' and 'wide leg' are your top converting signals - buyers need to know how these translate to actual measurements.",
      impact: "Reduces returns and improves conversion on fit-led searches",
      actions: ["Add garment measurements per size (chest, length, waist)", "Clarify fit vs standard: 'runs large', 'fits true to size'", "Include model height and size worn in image alt text", "Consider a fit comparison chart for repeat buyers"],
    },
    {
      priority: "med",
      icon: "🎥",
      title: "Video content for LLM and social channels",
      detail: "YouTube and TikTok signals are driving high-value phrases into your copy - but your product pages have no video. LLMs increasingly cite video-backed products. Short styling videos also generate fresh signal data for the next scan.",
      impact: "Strengthens AEO / AI Overview eligibility",
      actions: ["30-60s styling reel per SKU (TikTok + Reels native format)", "Upload to YouTube with keyword-matched title and description", "Embed on product page for dwell time signal", "Submit to Google via product video schema"],
    },
    {
      priority: "low",
      icon: "🔗",
      title: "Canonical URLs and internal linking",
      detail: "15 SKUs still lack canonical tags. Collection pages should link to these product pages with anchor text matching your top signal phrases to pass relevance signals.",
      impact: "Minor SEO benefit, prevents duplicate content risk",
      actions: ["Set canonical tag on all product pages", "Update collection page descriptions to include signal phrases", "Add cross-links between complementary SKUs (e.g. hoodie + cargo trousers)"],
    },
    {
      priority: "low",
      icon: "📊",
      title: "Customer data to replace benchmarks",
      detail: "Revenue projections are currently based on industry benchmarks (AOV £120, CVR 1.5%). Connecting your Shopify analytics will replace these with your actual conversion rate, average order value and return rate for more accurate uplift modelling.",
      impact: "More accurate revenue projections per SKU",
      actions: ["Connect Shopify Analytics in settings", "Verify GA4 e-commerce tracking is firing correctly", "Set up Shopify/GA4 integration for real CVR and AOV per product"],
    },
  ];

  const sevColor  = s => s === "high" ? "#dc2626" : s === "med" ? "#d97706" : "#6b7280";
  const sevBg     = s => s === "high" ? "#fef2f2" : s === "med" ? "#fffbeb" : "#f9fafb";
  const sevBorder = s => s === "high" ? "#fecaca" : s === "med" ? "#fde68a" : BORDER;
  const sevLabel  = s => s === "high" ? "High priority" : s === "med" ? "Medium" : "Low";

  return (
    <>
      <PageHead num="04" title="Post-Push Feed Review" sub="Your feed score after this batch. See what improved, what changed, and what you need to do manually to push further." onNext={onNext} nextLabel="GMC Fix →" />

      {/* Tab switcher */}
      <div data-testid="feed-review-tabs" style={{ display: "flex", gap: 0, border: `1px solid ${BORDER}`, borderRadius: 7, overflow: "hidden", marginBottom: 20, width: "fit-content" }}>
        {[
          { key: "audit",   label: "Feed Score" },
          { key: "gaps",    label: `Keyword Gaps (${prods.reduce((s, p) => s + (KEYWORD_GAPS[p.id]?.length || 0), 0)})` },
          { key: "changes", label: "What Changed" },
          { key: "manual",  label: `Manual Actions (${manualActions.length})` },
        ].map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            style={{ border: "none", background: activeTab === t.key ? BLACK : BG, color: activeTab === t.key ? "#fff" : MID, padding: "8px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s", borderRight: `1px solid ${BORDER}` }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── FEED SCORE TAB ── */}
      {activeTab === "audit" && (
        <>
          {/* Score hero - before vs after */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 0, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
            {/* Before */}
            <div style={{ padding: "28px 24px", background: "#fef2f2", textAlign: "center", borderRight: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 16 }}>Before this batch</div>
              <BigRing score={scoreBefore} animated={true} trackColor="#fecaca" />
              <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>Poor</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>47 SKUs · 8 issues identified</div>
            </div>

            {/* Arrow + delta */}
            <div style={{ padding: "28px 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#fafafa", borderRight: `1px solid ${BORDER}`, minWidth: 120 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>→</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: G }}>+{scoreAfter - scoreBefore}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: G, textTransform: "uppercase", letterSpacing: "0.5px" }}>pts gained</div>
              <div style={{ fontSize: 11, color: MID, marginTop: 8, textAlign: "center", lineHeight: 1.5 }}>{prods.length} SKU{prods.length !== 1 ? "s" : ""} selected · {totFix} GMC fixes</div>
            </div>

            {/* After */}
            <div style={{ padding: "28px 24px", background: GL, textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 16 }}>After this batch</div>
              <BigRing score={animated ? scoreAfter : scoreBefore} animated={animated} trackColor={GB} />
              <div style={{ fontSize: 13, fontWeight: 700, color: G }}>{scoreAfter >= 70 ? "Good" : "Needs work"}</div>
              <div style={{ fontSize: 12, color: MID, marginTop: 4 }}>Automated changes applied</div>
            </div>
          </div>

          {/* Dimension breakdown */}
          <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", marginBottom: 20 }}>
            <div style={{ padding: "12px 16px", background: "#fafafa", borderBottom: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.8px" }}>Score breakdown - what moved and why</div>
            </div>
            {dimensions.map((d, i) => {
              const delta = d.after - d.before;
              return (
                <div key={d.label} style={{ padding: "14px 16px", borderBottom: i < dimensions.length - 1 ? `1px solid ${BORDER}` : "none", background: delta > 0 ? "#fafffe" : BG }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, minWidth: 160 }}>{d.label}</div>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                      {/* Before bar */}
                      <div style={{ flex: 1, height: 8, borderRadius: 4, background: BORDER, position: "relative", overflow: "hidden" }}>
                        <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${d.before}%`, background: "#fca5a5", borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 11, color: LIGHT, minWidth: 28, textAlign: "right" }}>{d.before}</span>
                    </div>
                    <div style={{ fontSize: 14, color: LIGHT }}>→</div>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                      {/* After bar */}
                      <div style={{ flex: 1, height: 8, borderRadius: 4, background: BORDER, position: "relative", overflow: "hidden" }}>
                        <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: animated ? `${d.after}%` : `${d.before}%`, background: scoreColor(d.after), borderRadius: 4, transition: "width 1s ease" }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(d.after), minWidth: 28 }}>{d.after}</span>
                    </div>
                    {delta > 0
                      ? <span style={{ fontSize: 11, fontWeight: 800, color: G, background: GL, border: `1px solid ${GB}`, borderRadius: 4, padding: "2px 7px", minWidth: 44, textAlign: "center" }}>+{delta}</span>
                      : <span style={{ fontSize: 11, fontWeight: 600, color: LIGHT, background: "#f3f4f6", borderRadius: 4, padding: "2px 7px", minWidth: 44, textAlign: "center" }}>-</span>
                    }
                  </div>
                  {delta > 0 && <div style={{ fontSize: 11, color: MID, paddingLeft: 176 }}>{d.why}</div>}
                </div>
              );
            })}
          </div>

          {/* Supporting stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
            {[
              { label: "Est. revenue uplift / mo", val: `£${totRev.toLocaleString()}`, color: G,         sub: `${(BENCHMARKS.CVR*100).toFixed(1)}% CVR x £${BENCHMARKS.AOV} AOV` },
              { label: "Extra impressions / mo",   val: `+${totImp.toLocaleString()}`, color: "#4285f4", sub: "GSC baseline vs projected" },
              { label: "GMC fixes applied",        val: totFix,                        color: "#6c63ff", sub: "attributes + title quality" },
              { label: "Signals embedded",         val: totSig,                        color: "#f59e0b", sub: "consumer phrases in copy" },
            ].map((x, i) => (
              <div key={x.label} style={{ padding: "16px", borderRight: i < 3 ? `1px solid ${BORDER}` : "none", background: "#fafafa", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: x.color, marginBottom: 3 }}>{x.val}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>{x.label}</div>
                <div style={{ fontSize: 10, color: LIGHT }}>{x.sub}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: LIGHT, fontStyle: "italic", marginBottom: 20 }}>Revenue projections use industry benchmarks (AOV £{BENCHMARKS.AOV}, CVR {(BENCHMARKS.CVR*100).toFixed(1)}%). In production these pull from your Shopify store data.</div>
        </>
      )}

      {/* ── KEYWORD GAPS TAB ── */}
      {activeTab === "gaps" && (
        <div data-testid="keyword-gaps-tab" style={{ marginBottom: 20 }}>
          {/* Summary banner */}
          <div style={{ padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#fee2e2", border: "1px solid #fecaca", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: "#dc2626", flexShrink: 0 }}>
              {prods.reduce((s, p) => s + (KEYWORD_GAPS[p.id]?.filter(g => g.gap === "critical" || g.gap === "high").length || 0), 0)}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#991b1b", marginBottom: 2 }}>High-value keywords missing from your feed</div>
              <div style={{ fontSize: 12, color: "#991b1b", lineHeight: 1.5 }}>
                These search terms have strong volume and intent but aren't covered in your product titles, descriptions, or meta fields. Each gap is a missed impression.
              </div>
            </div>
          </div>

          {/* Per-SKU keyword gaps */}
          {prods.map(p => {
            const gaps = KEYWORD_GAPS[p.id] || [];
            if (!gaps.length) return null;
            const criticalCount = gaps.filter(g => g.gap === "critical").length;
            const highCount = gaps.filter(g => g.gap === "high").length;
            const partialCount = gaps.filter(g => g.gap === "partial").length;

            return (
              <div key={p.id} data-testid={`keyword-gaps-${p.id}`} style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", marginBottom: 14 }}>
                {/* SKU header */}
                <div style={{ padding: "12px 16px", background: "#fafafa", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: BLACK }}>{p.title}</span>
                    <span style={{ fontSize: 11, color: LIGHT }}>{p.sku}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {criticalCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 3, padding: "2px 7px" }}>{criticalCount} critical</span>}
                    {highCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: "#d97706", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 3, padding: "2px 7px" }}>{highCount} high</span>}
                    {partialCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: "#4285f4", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 3, padding: "2px 7px" }}>{partialCount} partial</span>}
                  </div>
                </div>

                {/* Gap table header */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 65px 50px 50px 1fr", padding: "8px 16px", background: "#f9fafb", borderBottom: `1px solid ${BORDER}`, gap: 8 }}>
                  {["Keyword", "Volume", "Intent", "Title", "Desc", "Recommendation"].map(h => (
                    <div key={h} style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</div>
                  ))}
                </div>

                {/* Gap rows */}
                {gaps.map((g, gi) => {
                  const gapColor = g.gap === "critical" ? "#dc2626" : g.gap === "high" ? "#d97706" : g.gap === "partial" ? "#4285f4" : "#6b7280";
                  const gapBg = g.gap === "critical" ? "#fef2f2" : g.gap === "high" ? "#fffbeb" : g.gap === "partial" ? "#eff6ff" : "#f9fafb";
                  const intentColor = g.intent === "high" ? "#dc2626" : g.intent === "med" ? "#d97706" : "#6b7280";
                  const sm = SOURCE_META[g.source];

                  return (
                    <div key={`gap-${gi}-${g.query}`} style={{ display: "grid", gridTemplateColumns: "1fr 90px 65px 50px 50px 1fr", padding: "10px 16px", borderBottom: i < gaps.length - 1 ? `1px solid ${BORDER}` : "none", background: g.gap === "critical" ? "#fffafa" : BG, gap: 8, alignItems: "center" }}>
                      {/* Keyword + source */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: gapColor, background: gapBg, border: `1px solid ${gapColor}30`, borderRadius: 3, padding: "1px 5px", textTransform: "uppercase", letterSpacing: "0.3px", flexShrink: 0 }}>
                          {g.gap}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>{g.phrase}</span>
                        {sm && <span style={{ fontSize: 9, fontWeight: 700, color: sm.color, background: sm.bg, borderRadius: 3, padding: "1px 5px", flexShrink: 0 }}>{sm.label}</span>}
                      </div>
                      {/* Volume */}
                      <div style={{ fontSize: 12, fontWeight: 600, color: MID }}>{g.volume}</div>
                      {/* Intent */}
                      <span style={{ fontSize: 10, fontWeight: 700, color: intentColor, background: `${intentColor}12`, borderRadius: 3, padding: "2px 6px", textTransform: "uppercase", width: "fit-content" }}>{g.intent}</span>
                      {/* In title */}
                      <div style={{ fontSize: 12, fontWeight: 700, color: g.inTitle ? G : "#dc2626", textAlign: "center" }}>{g.inTitle ? "\u2713" : "\u2717"}</div>
                      {/* In desc */}
                      <div style={{ fontSize: 12, fontWeight: 700, color: g.inDesc ? G : "#dc2626", textAlign: "center" }}>{g.inDesc ? "\u2713" : "\u2717"}</div>
                      {/* Recommendation */}
                      <div style={{ fontSize: 11, color: MID, lineHeight: 1.4 }}>{g.suggestion}</div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Gap legend */}
          <div style={{ padding: "10px 16px", background: "#f9fafb", border: `1px solid ${BORDER}`, borderRadius: 6, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.5px" }}>Gap severity:</span>
            {[
              { label: "Critical", color: "#dc2626", desc: "High volume, missing from title + desc" },
              { label: "High", color: "#d97706", desc: "Missing from both fields" },
              { label: "Partial", color: "#4285f4", desc: "In desc but missing from title/meta" },
              { label: "Low", color: "#6b7280", desc: "Low volume, nice-to-have" },
            ].map(l => (
              <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: l.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: l.color }}>{l.label}</span>
                <span style={{ fontSize: 10, color: LIGHT }}>{l.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WHAT CHANGED TAB ── */}
      {activeTab === "changes" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
          {/* Per-SKU change cards */}
          {prods.map(p => {
            const skuFixes = Object.entries(fixes).filter(([k, v]) => k.startsWith(`${p.id}-`) && v);
            return (
              <div key={p.id} style={{ border: `1px solid ${GB}`, borderRadius: 8, overflow: "hidden" }}>
                {/* SKU header */}
                <div style={{ padding: "12px 16px", background: GL, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{p.title}</span>
                    <span style={{ fontSize: 11, color: LIGHT, marginLeft: 8 }}>{p.sku}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Ring score={p.impact.scoreBefore} size={34} />
                    <span style={{ fontSize: 13, color: LIGHT }}>→</span>
                    <Ring score={p.impact.scoreAfter} size={34} />
                    <span style={{ fontSize: 12, fontWeight: 800, color: G, marginLeft: 4 }}>+{p.impact.scoreAfter - p.impact.scoreBefore} pts</span>
                  </div>
                </div>

                {/* Change rows */}
                <div style={{ borderTop: `1px solid ${GB}` }}>
                  {[
                    { icon: "✓", label: "Title optimised",      before: p.title,              after: generatedCopy[p.id]?.optimised_title || p.optimisedTitle || p.title, type: "copy" },
                    { icon: "✓", label: "Description written",  before: "No description",      after: (generatedCopy[p.id]?.optimised_description || p.optimisedDesc || p.description || "").split("\n")[0] + "...", type: "copy" },
                    { icon: "✓", label: "Signals embedded",     before: null,                  after: `${p.signalAttribution?.length || 0} consumer phrases - ${p.signalAttribution?.filter(a => a.sources?.length >= 3).length || 0} multi-source`, type: "signal" },
                    { icon: "✓", label: "Meta title generated", before: "Not set",             after: generatedCopy[p.id]?.meta_title || `${p.title} | ${p.brand} | UK`, type: "meta" },
                    ...(skuFixes.length > 0 ? [{ icon: "✓", label: `${skuFixes.length} GMC attribute${skuFixes.length > 1 ? "s" : ""} fixed`, before: null, after: skuFixes.map(([k]) => k.split("-").slice(1).join("-")).join(", "), type: "gmc" }] : []),
                    ...(p.attributeMatches?.filter(m => m.status === "gap").length > 0 ? [{
                      icon: "!", label: `${p.attributeMatches.filter(m => m.status === "gap").length} attribute gap${p.attributeMatches.filter(m => m.status === "gap").length > 1 ? "s" : ""} remain`,
                      before: null, after: p.attributeMatches.filter(m => m.status === "gap").map(m => m.field).join(", ") + " - needs manual input",
                      type: "gap"
                    }] : []),
                  ].map((row, i) => {
                    const typeColor = { copy: G, signal: "#6c63ff", meta: "#4285f4", gmc: "#d97706", gap: "#dc2626" }[row.type];
                    const typeBg    = { copy: GL, signal: "#f5f3ff", meta: "#eff6ff", gmc: "#fffbeb", gap: "#fef2f2" }[row.type];
                    return (
                      <div key={`${row.type}-${row.label}`} style={{ display: "grid", gridTemplateColumns: "20px 140px 1fr 16px 1fr", alignItems: "flex-start", gap: 10, padding: "10px 16px", borderTop: `1px solid ${BORDER}`, background: row.type === "gap" ? "#fffafa" : BG }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: typeColor, marginTop: 1 }}>{row.icon}</span>
                        <div style={{ fontSize: 11, fontWeight: 700, color: MID, paddingTop: 2 }}>{row.label}</div>
                        {row.before
                          ? <div style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", lineHeight: 1.4, paddingTop: 2 }}>{row.before.length > 80 ? row.before.slice(0,80) + "..." : row.before}</div>
                          : <div />
                        }
                        {row.before ? <span style={{ color: BORDER, paddingTop: 2, fontSize: 12 }}>→</span> : <div />}
                        <div style={{ fontSize: 11, color: typeColor, fontWeight: 600, lineHeight: 1.4, background: typeBg, border: `1px solid ${typeColor}22`, borderRadius: 4, padding: "2px 7px" }}>
                          {row.after.length > 100 ? row.after.slice(0,100) + "..." : row.after}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── MANUAL ACTIONS TAB ── */}
      {activeTab === "manual" && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ padding: "12px 16px", background: "#fffbeb", border: `1px solid #fde68a`, borderRadius: 8, marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ fontSize: 16 }}>💡</span>
            <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
              <strong>These can't be automated.</strong> Attune has pushed every change it can - copy, attributes, meta fields and signals. What's below requires brand input, creative assets, or third-party integrations. Each one has a material impact on feed performance and LLM discoverability.
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {manualActions.map((action, i) => (
              <div key={`action-${action.label}`} style={{ border: `1px solid ${sevBorder(action.priority)}`, borderLeft: `3px solid ${sevColor(action.priority)}`, borderRadius: 8, overflow: "hidden", background: BG }}>
                {/* Action header */}
                <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{action.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: BLACK }}>{action.title}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: sevColor(action.priority), background: sevBg(action.priority), border: `1px solid ${sevBorder(action.priority)}`, borderRadius: 3, padding: "2px 7px", textTransform: "uppercase", letterSpacing: "0.4px" }}>{sevLabel(action.priority)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6, marginBottom: 8 }}>{action.detail}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: sevColor(action.priority), background: sevBg(action.priority), border: `1px solid ${sevBorder(action.priority)}`, borderRadius: 5, padding: "4px 10px", display: "inline-block" }}>
                      Impact: {action.impact}
                    </div>
                  </div>
                </div>

                {/* Action checklist */}
                <div style={{ borderTop: `1px solid ${BORDER}`, padding: "10px 16px 12px 52px", background: "#fafafa" }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 7 }}>Steps to action</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {action.actions.map((a, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${BORDER}`, background: BG, flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 12, color: MID, lineHeight: 1.5 }}>{a}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Footer onNext={onNext} nextLabel="GMC Fix →" />
    </>
  );
}


// ─── EXPORT PANEL (extracted from IIFE to avoid hooks-in-callback) ───────────

function ExportPanel({ prods, analyticsConnected, handleConnectAnalytics, analyticsConnecting, realCVR, realAOV, generatedCopy = {} }) {
  const [copied, setCopied] = useState(null);
  const getT = (p) => generatedCopy[p.id]?.optimised_title || p.optimisedTitle || p.title;
  const getD = (p) => generatedCopy[p.id]?.optimised_description || p.optimisedDesc || p.description || "";
  const copyToClipboard = (pid) => {
    const p = prods.find(x => x.id === pid);
    const text = [
      `SKU: ${p.sku}`,
      `Title: ${getT(p)}`,
      `Description: ${getD(p)}`,
      `Meta title: ${generatedCopy[p.id]?.meta_title || getT(p) + " | " + p.brand}`,
      `Tags: ${[...(p.shopifyData?.tags || [])].join(", ")}`,
      `Signals used: ${(p.signalAttribution || []).map(s => s.phrase).join(", ")}`,
    ].join("\n\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(pid);
      setTimeout(() => setCopied(null), 2000);
    });
  };
  const downloadCSV = () => {
    const header = "SKU,Title,Description,Meta Title,Signals Used";
    const rows = prods.map(p =>
      [p.sku, `"${getT(p)}"`, `"${getD(p).replace(/"/g,'""').replace(/\n/g,' ')}"`, `"${generatedCopy[p.id]?.meta_title || getT(p) + " | " + p.brand}"`, `"${(p.signalAttribution||[]).map(s=>s.phrase).join(', ')}"`].join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a"); a.href = url; a.download = "sift-feed-output.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ padding: "12px 18px", background: "#fafafa", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.8px" }}>Export optimised copy</div>
        <button onClick={downloadCSV} style={{ background: G, color: "#fff", border: "none", borderRadius: 5, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.2px" }}>
          Download CSV
        </button>
      </div>
      <div style={{ padding: "12px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
        {prods.map(p => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", border: `1px solid ${BORDER}`, borderRadius: 7, background: BG }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: BLACK, marginBottom: 2 }}>{p.title} <span style={{ fontWeight: 400, color: LIGHT, fontSize: 11 }}>{p.sku}</span></div>
              <div style={{ fontSize: 11, color: MID, maxWidth: 480, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.optimisedTitle}</div>
            </div>
            <button onClick={() => copyToClipboard(p.id)}
              style={{ background: copied === p.id ? GL : BG, color: copied === p.id ? G : MID, border: `1px solid ${copied === p.id ? GB : BORDER}`, borderRadius: 5, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.2s" }}>
              {copied === p.id ? "✓ Copied" : "Copy copy"}
            </button>
          </div>
        ))}
      </div>
      {/* Shopify Analytics CTA */}
      {!analyticsConnected ? (
        <div style={{ borderTop: `1px solid #fde68a`, padding: "12px 18px", background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>Revenue projections are using industry benchmarks (CVR 0.8%, AOV £120). </span>
            <span style={{ fontSize: 12, color: "#92400e" }}>Connect your Shopify Analytics to replace with your real store conversion rate and AOV - projections will update automatically.</span>
          </div>
          <button onClick={handleConnectAnalytics} disabled={analyticsConnecting}
            style={{ background: analyticsConnecting ? "#b45309" : "#92400e", color: "#fff", border: "none", borderRadius: 5, padding: "7px 14px", fontSize: 11, fontWeight: 700, cursor: analyticsConnecting ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap", opacity: analyticsConnecting ? 0.8 : 1 }}>
            {analyticsConnecting ? "Connecting…" : "Connect Analytics"}
          </button>
        </div>
      ) : (
        <div style={{ borderTop: `1px solid ${GB}`, padding: "12px 18px", background: GL, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14 }}>✅</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: G }}>Using real store data - </span>
          <span style={{ fontSize: 12, color: G }}>CVR {(realCVR * 100).toFixed(1)}% · AOV £{realAOV} · Projections reflect your actual store performance</span>
        </div>
      )}
    </div>
  );
}

// ─── STEP 07 ─────────────────────────────────────────────────────────────────


